import { useMutation } from 'react-query';
import { shallow } from 'zustand/shallow';
import usePlanStore from '../../../../../../store/planStore';
import { getYouTubeConfig } from '../../../../../../api/services/mediaPlanning/touchpointConfigs';
import { useError } from '../../../common/Errors/ErrorContext/ErrorContext';
import { YTTargetGroup } from '../interfaces';

const useYoutubeConfig = () => {
  const maxSliderValue = 1;

  const {
    generalInfoState,
    countryForms,
    selectedCountry,
    selectedCountryISOCode,
    updateYouTubeConfig,
  } = usePlanStore();

  const { showError } = useError();

  const selectedCountryForm = countryForms[selectedCountry!];
  const youTubeTargetGroup =
    selectedCountryForm?.youTubeConfig?.targetGroup || undefined;

  const selectedCountryFormSubset = usePlanStore((state) => {
    const form = state.savedCountryForms[state.selectedCountry!];
    return form
      ? {
          targetGroup: form.targetGroup,
          campaignScenario: form.campaignScenario,
          segments: form.segments,
          startDate: form.startDate,
          campaignLength: form.campaignLength,
          isCountrySetupConfirmed: form.isCountrySetupConfirmed,
        }
      : null;
  }, shallow);

  const {
    mutate: fetchYoutubeConfig,
    isLoading,
    data,
  } = useMutation({
    mutationFn: (youtubeTargetGroup?: YTTargetGroup) => {
      if (
        !generalInfoState.division ||
        !selectedCountry ||
        !selectedCountryISOCode ||
        !selectedCountryForm?.campaignScenario
      ) {
        return Promise.reject(
          new Error('Missing required parameters for YouTube config.')
        );
      }

      return getYouTubeConfig(
        generalInfoState.division,
        selectedCountryISOCode,
        selectedCountryForm.targetGroup,
        selectedCountryForm.campaignScenario,
        youTubeTargetGroup?.targetGroup
      );
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || error.message || 'Unknown error';
      showError(errorMessage);
    },
    onSuccess: (response, youtubeTargetGroup) => {
      if (selectedCountry) {
        updateYouTubeConfig(selectedCountry, {
          bumperValue: response.data.bumperDefault ?? 0,
          bumperMin: response.data.bumperMin,
          bumperMax: response.data.bumperMax,
          bumperDefault: response.data.bumperDefault,
          unSkipValue: response.data.unskipDefault ?? 0,
          unskipMin: response.data.unskipMin,
          unskipMax: response.data.unskipMax,
          unskipDefault: response.data.unskipDefault,
          recommendedTargetGroup: response.data.recommendedTargetGroup,
          skipValue: Math.max(
            maxSliderValue -
              (response?.data?.unskipDefault || 0) -
              (response?.data?.bumperDefault || 0),
            0
          ),
          saturation: response?.data?.saturationDefault,
          targetGroup: response.data.targetGroups?.find(
            (tg: YTTargetGroup) =>
              tg.targetGroup ===
              (youtubeTargetGroup?.targetGroup ??
                response.data.recommendedTargetGroup)
          ),
          partnerBudgetShare: 0,
          saturationMin: response?.data?.saturationMin ?? null,
          saturationMax: response?.data?.saturationMax ?? null,
          targetGroups: response?.data?.targetGroups ?? [],
          minReach: response?.data?.minReach ?? null,
        });
      }
    },
  });

  return {
    fetchYoutubeConfig,
    isFetching: isLoading,
    data,
  };
};

export default useYoutubeConfig;
